/**
 * /join?token=… and /confirm?token=… — magic-link landing for waitlist patrons.
 *
 * Flow:
 *   1. Page mounts, reads ?token=… from URL
 *   2. Calls /api/waitlist/magic/{token} to get name + email
 *   3. User sets password + phone + DOB → /api/waitlist/magic/complete
 *   4. Email OTP shown → /api/waitlist/verify-email-otp
 *   5. Token stored in localStorage; redirect to /panelist/profile to finish DNA
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import DobPicker from "../components/DobPicker";

export default function JoinPatron() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { setUser } = useAuth();

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("loading");  // loading | form | otp | done
  const [form, setForm] = useState({ password: "", phone_number: "", dob: "" });
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing confirmation token in the link."); setStep("error"); return; }
    api.get(`/waitlist/magic/${token}`)
      .then((r) => {
        setInfo(r.data);
        // If the visitor is already a verified patron (came in via /register
        // before clicking the magic link OR clicked the magic link a second
        // time after onboarding), don't show the founding-patron join form.
        // Show a friendly "you're already a member — sign in" panel instead.
        setStep(r.data?.already_patron ? "already_patron" : "form");
      })
      .catch((e) => { setError(e.response?.data?.detail || "Invalid or expired link."); setStep("error"); });
  }, [token]);

  const submitProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/waitlist/magic/complete", { token, ...form });
      setStep("otp");
      toast.success("Check your email for a 6-digit code.");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/waitlist/verify-email-otp", { email: info.email, code: otp });
      // CRITICAL: use the same localStorage key the rest of the app reads from
      // (api client + auth context both look for `pollpak_token`). The old code
      // wrote to `token` which caused every subsequent authenticated call —
      // including the profile-save — to fail with 401 "Not authenticated".
      localStorage.setItem("pollpak_token", r.data.token);
      setUser && setUser(r.data.user);
      toast.success("Welcome aboard! Complete your profile to start earning.");
      setStep("done");
      setTimeout(() => nav("/panelist/profile", { replace: true }), 1200);
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };

  if (step === "loading") return <Frame><div className="text-neutral-500">Looking up your invitation…</div></Frame>;
  if (step === "error") return <Frame><div className="text-red-600">{error}</div></Frame>;
  if (step === "already_patron") {
    return (
      <Frame>
        <div className="space-y-4" data-testid="already-patron">
          <div>
            <div className="overline text-mango">YOU&apos;RE ALREADY IN</div>
            <h1 className="text-3xl font-black tracking-tight mt-1">Welcome back, {(info?.name || "").split(" ")[0]}</h1>
          </div>
          <p className="text-sm text-neutral-600 leading-relaxed">
            Looks like you&apos;ve already completed your oodel sign-up with <span className="font-mono">{info?.email}</span>. No need to set up again — just sign in to take this survey and start earning points.
          </p>
          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={() => nav("/login")} className="bg-mango hover:bg-mango/90 text-white border-2 border-black rounded-none" data-testid="already-patron-signin">
              Sign in &rarr;
            </Button>
            <Button onClick={() => nav("/")} variant="ghost" className="rounded-none text-xs">Go to homepage</Button>
          </div>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      {step === "form" && (
        <form onSubmit={submitProfile} className="space-y-4" data-testid="join-form">
          <div>
            <div className="text-2xl font-black tracking-tight">Welcome, {info.name}</div>
            <div className="text-sm text-neutral-500">{info.email}</div>
          </div>
          <div>
            <Label>Set a password (min 6 chars)</Label>
            <Input type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="join-password" />
          </div>
          <div>
            <Label>Phone number</Label>
            <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="+92 3XX XXXXXXX" required data-testid="join-phone" />
            <p className="text-xs text-neutral-500 mt-1">We won't SMS you until you redeem a reward or win a raffle.</p>
          </div>
          <div>
            <Label>Date of birth</Label>
            <DobPicker value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} required testIdPrefix="join-dob" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-mango hover:bg-mango/90 text-white" data-testid="join-submit">{busy ? "Saving…" : "Continue →"}</Button>
        </form>
      )}
      {step === "otp" && (
        <form onSubmit={verifyOtp} className="space-y-4" data-testid="join-otp-form">
          <div>
            <div className="text-2xl font-black tracking-tight">Verify your email</div>
            <div className="text-sm text-neutral-500">We sent a 6-digit code to <b>{info.email}</b>.</div>
          </div>
          <Input value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} className="text-2xl font-mono text-center tracking-widest" placeholder="000000" required data-testid="join-otp" />
          <Button type="submit" disabled={busy} className="w-full bg-mango hover:bg-mango/90 text-white" data-testid="join-verify">{busy ? "Verifying…" : "Verify & enter →"}</Button>
        </form>
      )}
      {step === "done" && (
        <div className="text-center" data-testid="join-done">
          <div className="text-3xl mb-2">🎉</div>
          <div className="text-xl font-bold">You're in!</div>
          <div className="text-sm text-neutral-500 mt-2">Redirecting you to complete your profile…</div>
        </div>
      )}
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white border-2 border-black p-8 hard-shadow">
        <div className="overline text-mango mb-2">FOUNDING PATRON · JOIN</div>
        {children}
      </div>
    </div>
  );
}
